import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useAuthModal } from "../../App";
import { useGameSession, useMidnightCountdown, MAX_ATTEMPTS } from "../../hooks/useGameSession";
import { useNextChallenge } from "../../hooks/useNextChallenge";
import ResultModal from "../../components/ResultModal";
import AchievementPopup from "../../components/AchievementPopup";
import Tooltip from "../../components/Tooltip";
import AttemptRow from "../../components/AttemptRow";
import HintTile from "../../components/HintTile";
import ConfirmDialog from "../../components/ConfirmDialog";
import "../../styles/GuessSpellPage.css";

const CLASS_SLUG = {
  "Warrior": "warrior", "Paladin": "paladin", "Hunter": "hunter",
  "Rogue": "rogue", "Priest": "priest", "Shaman": "shaman",
  "Mage": "mage", "Warlock": "warlock", "Druid": "druid",
  "Death Knight": "death-knight", "Monk": "monk",
  "Demon Hunter": "demon-hunter", "Evoker": "evoker",
};

const HINT_CLASS_AT = 3, HINT_LETTER_AT = 4, HINT_ROTATE_AT = 1, HINT_COLOR_AT = 2;

async function preloadSpellIcon(daily) {
  if (daily?.icon_url) {
    await new Promise((resolve) => {
      const img = new Image(); img.onload = resolve; img.onerror = resolve; img.src = daily.icon_url;
    });
  }
}

export default function GuessSpellPage() {
  const navigate = useNavigate();
  const { open: openAuth } = useAuthModal();
  const countdown = useMidnightCountdown();

  const s = useGameSession({
    gameName: "spells", itemIdKey: "spell_id",
    correctNameKey: "correct_spell_name", dailyKey: "daily_spell",
    preloadDaily: preloadSpellIcon,
  });

  // ── Next Challenge tracking ──
  const { markCompleted, nextChallenge, allCompleted } = useNextChallenge("spells", s.gameMode);

  React.useEffect(() => {
    if (s.game.finished) markCompleted();
  }, [s.game.finished, markCompleted]);

  const classSlug = CLASS_SLUG[s.game.hints?.class_hint] || "";

  const classTooltip = useMemo(() => {
    if (s.game.hints.class_hint) return [{ icon: "\u2726", text: "Class: " + s.game.hints.class_hint, type: "highlight" }];
    if (s.attemptsUsed >= HINT_CLASS_AT) return [{ icon: "\u23f3", text: "Revealed \u2014 make a guess to refresh", type: "muted" }];
    const rem = Math.max(0, HINT_CLASS_AT - s.attemptsUsed);
    return [
      { icon: "\ud83d\udd12", text: "Class hint", type: "title" },
      { text: "Available after " + HINT_CLASS_AT + " attempts", type: "muted" },
      { text: rem + " attempt" + (rem === 1 ? "" : "s") + " remaining", type: "countdown" },
    ];
  }, [s.game.hints.class_hint, s.attemptsUsed]);

  const letterTooltip = useMemo(() => {
    if (s.game.hints.first_letter_hint) return [{ icon: "\u2726", text: "First letters: " + s.game.hints.first_letter_hint, type: "highlight" }];
    if (s.attemptsUsed >= HINT_LETTER_AT) return [{ icon: "\u23f3", text: "Revealed \u2014 make a guess to refresh", type: "muted" }];
    const rem = Math.max(0, HINT_LETTER_AT - s.attemptsUsed);
    return [
      { icon: "\ud83d\udd12", text: "First Letters hint", type: "title" },
      { text: "Available after " + HINT_LETTER_AT + " attempts", type: "muted" },
      { text: rem + " attempt" + (rem === 1 ? "" : "s") + " remaining", type: "countdown" },
    ];
  }, [s.game.hints.first_letter_hint, s.attemptsUsed]);

  const iconTooltipLines = useMemo(() => {
    if (s.attemptsUsed === 0) return [
      { icon: "\ud83d\udd2e", text: "Spell Icon", type: "title" },
      { text: "After attempt " + HINT_ROTATE_AT + ": icon will be rotated", type: "muted" },
      { text: "After attempt " + HINT_COLOR_AT + ": icon will be in full color", type: "muted" },
    ];
    if (s.attemptsUsed < HINT_COLOR_AT) {
      const rem = Math.max(0, HINT_COLOR_AT - s.attemptsUsed);
      return [
        { icon: "\ud83d\udd04", text: "Icon is now rotated", type: "active" },
        { text: "After attempt " + HINT_COLOR_AT + ": full color revealed", type: "muted" },
        { text: rem + " attempt" + (rem === 1 ? "" : "s") + " until color", type: "countdown" },
      ];
    }
    return [
      { icon: "\ud83c\udfa8", text: "Icon is in full color", type: "active" },
      { text: "All visual hints are now revealed", type: "muted" },
    ];
  }, [s.attemptsUsed]);

  return (
    <div className="gs-root">
      <Helmet>
        <title>Azerodle | Guess the Spell</title>
        <meta name="description" content="Recognize a WoW spell by its icon. A daily challenge for World of Warcraft fans." />
      </Helmet>
      <div className="gs-wrapper">
        {s.isGuest && (
          <div className="gs-guest-banner">
            <span>Playing as <strong>guest</strong> - rewards and statistics unavailable</span>
            <button className="gs-ghost-btn" onClick={openAuth}>Sign up</button>
          </div>
        )}
        <div className="gs-page-title"><h1 className="gs-title">Guess the Spell Icon</h1></div>

        {s.initialLoading ? (
          <div className="gs-panel gs-panel--ready">
            <div className="gs-panel-spinner"><div className="gs-spinner" /></div>
          </div>
        ) : (
          <div className="gs-panel gs-panel--ready">
            <div className="gs-panel-content">
              <div className="gs-mode-row">
                {["classic", "retail"].map(mode => (
                  <button key={mode} className={"gs-mode-btn " + (s.gameMode === mode ? "active" : "")} onClick={() => s.switchMode(mode)}>
                    {mode === "retail" ? "Retail" : "Classic"}
                  </button>
                ))}
              </div>
              <div className="gs-divider" />
              <div className={["gs-panel-body", s.showBody ? "gs-panel-body--ready" : "", s.fading ? "gs-panel-body--fading" : ""].join(" ")}>
              {s.contentReady && (<>
                {!s.game.finished && (<>
                  <div className="gs-icon-center">
                    <Tooltip lines={iconTooltipLines}>
                      <div className="gs-spell-icon-frame">
                        <span className="gs-frame-corner gs-frame-corner--tl" />
                        <span className="gs-frame-corner gs-frame-corner--tr" />
                        <span className="gs-frame-corner gs-frame-corner--bl" />
                        <span className="gs-frame-corner gs-frame-corner--br" />
                        <div className="gs-spell-icon-inner">
                          {s.dailyItem && <img key={s.iconKey} src={s.dailyItem.icon_url} alt="spell icon" className={"gs-spell-icon" + (s.attemptsUsed > 2 ? " gs-spell-icon--no-anim" : "")} />}
                        </div>
                      </div>
                    </Tooltip>
                  </div>
                  <div className="gs-hints-row">
                    <HintTile label="Class" value={s.game.hints.class_hint} revealed={!!s.game.hints.class_hint} className={"class-" + classSlug} tooltipLines={classTooltip} animateHints={s.game.shouldAnimateHints} />
                    <HintTile label="First Letters" value={s.game.hints.first_letter_hint} revealed={!!s.game.hints.first_letter_hint} tooltipLines={letterTooltip} animateHints={s.game.shouldAnimateHints} />
                  </div>
                  <div className="gs-progress-wrap">
                    <div className="gs-progress-meta">
                      <span className="gs-progress-label">Attempts</span>
                      <span className={"gs-progress-count " + (s.attemptsUsed >= MAX_ATTEMPTS ? "exhausted" : "")}>{s.attemptsUsed} / {MAX_ATTEMPTS}</span>
                    </div>
                    <div className="gs-progress-track">
                      <div className={"gs-progress-fill " + (s.game.shouldAnimateProgress ? "animate" : "")} style={{ width: s.game.progressWidth + "%" }} />
                    </div>
                  </div>
                </>)}
                {s.game.finished ? (
                  <div className={"gs-finished " + (s.didWin ? "win" : "lose")}>
                    <div className="gs-finished-icon">{s.didWin ? "\ud83c\udfc6" : "\ud83d\udc80"}</div>
                    <p className="gs-finished-title">{s.didWin ? "Already Solved!" : "Game Over"}</p>
                    <p className="gs-finished-sub">
                      {s.didWin
                        ? <>You guessed <strong>{s.correctAnswer?.user_answer || s.game.correctAnswerName}</strong> in <strong>{s.correctAnswer.attempt_number}</strong> {s.correctAnswer.attempt_number === 1 ? "attempt" : "attempts"}</>
                        : <>The spell was <strong>{s.game.correctAnswerName || "???"}</strong></>}
                    </p>
                    <div className="gs-finished-countdown">
                      <span className="gs-finished-countdown-label">Next spell in</span>
                      <span className="gs-finished-countdown-time">{countdown}</span>
                    </div>

                    {/* Next Challenge button */}
                    {nextChallenge ? (
                      <button
                        className="gs-btn-next-challenge"
                        onClick={() => {
                          if (nextChallenge.game === "spells") {
                            s.switchMode(nextChallenge.mode);
                          } else {
                            navigate(nextChallenge.path + "?mode=" + nextChallenge.mode);
                          }
                        }}
                      >
                        <span className="gs-btn-next-challenge-text">Next Challenge</span>
                      </button>
                    ) : allCompleted ? (
                      <div className="gs-all-done">
                        <span className="gs-all-done-text">All daily challenges completed!</span>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="gs-input-section">
                    <div className="gs-input-wrap">
                      <input className="gs-input" value={s.userAnswer} onChange={s.handleChange} onKeyDown={s.handleKeyDown} placeholder="Type a spell name..." autoComplete="off" disabled={s.submitting} />
                      {s.suggestions.length > 0 && (
                        <div className="gs-suggestions">
                          {s.suggestions.map(spell => (
                            <div key={spell.spell_id} className="gs-suggestion-item" onClick={() => s.handleSelect(spell)}>
                              {spell.icon_url && <img src={spell.icon_url} alt="" className="gs-suggestion-icon" />}
                              <span>{spell.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button className={"gs-btn-gold " + (s.submitting ? "submitting" : "")} onClick={s.makeGuess} disabled={!s.selectedItem || s.submitting}>
                      {s.submitting ? "Checking..." : "Submit Answer"}
                    </button>
                    <div className="gs-secondary-actions">
                      <button className="gs-btn-secondary gs-btn-skip" onClick={() => s.setConfirmAction("skip")} disabled={s.submitting}>Skip Attempt</button>
                      <button className="gs-btn-secondary gs-btn-surrender" onClick={() => s.setConfirmAction("surrender")} disabled={s.submitting}>Surrender</button>
                    </div>
                    {s.error && <p className="gs-error">{"\u26a0"} {s.error}</p>}
                  </div>
                )}
                {!s.game.finished && (<>
                  <div className="gs-divider" />
                  <div className="gs-history">
                    <p className="gs-history-label">Attempt History</p>
                    {Array.from({ length: MAX_ATTEMPTS }, (_, i) => {
                      const attempt = s.game.answers[i];
                      return attempt
                        ? <AttemptRow key={s.gameMode + "-" + attempt.attempt_number} attempt={attempt} index={i} animate={s.game.animateAttempts && i === s.game.answers.length - 1} />
                        : <div key={i} className="gs-attempt-row gs-attempt-empty"><span className="gs-attempt-num">#{i + 1}</span><span className="gs-attempt-empty-text">{"\u2014"}</span></div>;
                    })}
                  </div>
                </>)}
              </>)}
              </div>
            </div>
          </div>
        )}
      </div>
      {s.confirmAction && <ConfirmDialog type={s.confirmAction} onConfirm={s.handleConfirm} onCancel={s.handleCancelConfirm} />}
      {s.showModal && (
        <ResultModal result={s.lastResult} onClose={s.handleResultClose} isGuest={s.isGuest} onRegister={openAuth}
          itemName={s.correctAnswer?.user_answer || s.game.correctAnswerName} itemIcon={s.dailyItem?.icon_url}
          itemLabel="spell" gameName="spells" gameMode={s.gameMode}
          answers={s.game.answers} stats={s.stats} xpResult={s.isGuest ? null : s.xpResult} />
      )}
      {s.showAchievement && s.achievementQueue.length > 0 && (
        <AchievementPopup achievement={s.achievementQueue[0]} onClose={s.handleAchievementClose} />
      )}
    </div>
  );
}